import { NavLink, Link, useLocation } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { LANGS } from "@/i18n";
import { useEffect, useState } from "react";
import { User, Menu, X } from "lucide-react";

const NAV_ITEMS = [
  ["/properties", "nav.properties", "nav-properties"],
  ["/rentals", "nav.rentals", "nav-rentals"],
  ["/investments", "nav.investments", "nav-investments"],
  ["/off-market", "nav.offmarket", "nav-offmarket"],
  ["/services", "nav.services", "nav-services"],
  ["/about", "nav.about", "nav-about"],
  ["/contact", "nav.contact", "nav-contact"],
];

export default function Header() {
  const { t, lang, setLang } = useLanguage();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [loc.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const isHome = loc.pathname === "/";
  const light = scrolled || !isHome || mobileOpen;

  return (
    <>
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        light
          ? "bg-bg/95 backdrop-blur-xl border-b border-line"
          : "bg-black/30 backdrop-blur-md border-b border-white/10"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="brand-link"
          className={`flex items-baseline gap-2 ${light ? "text-ink" : "text-white"}`}
          style={light ? undefined : { textShadow: "0 1px 8px rgba(0,0,0,0.5)" }}
        >
          <span className="font-serif text-2xl tracking-tight leading-none">JK Invest</span>
          <span className="overline mt-0.5" style={{ color: "inherit", opacity: 0.7 }}>
            Group
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          {NAV_ITEMS.map(([to, key, testid]) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `nav-link text-sm font-medium ${light ? "text-ink" : "text-white"} ${
                  isActive ? "active" : ""
                }`
              }
              style={light ? undefined : { textShadow: "0 1px 6px rgba(0,0,0,0.5)" }}
            >
              {t(key)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <div
            className={`hidden sm:flex items-center gap-1 rounded-full px-1.5 py-1 text-xs font-medium ${
              light ? "bg-white/60 border border-line" : "bg-white/10 border border-white/20 text-white"
            }`}
            data-testid="lang-switcher"
          >
            {LANGS.map((l) => (
              <button
                key={l.code}
                data-testid={`lang-${l.code}`}
                onClick={() => setLang(l.code)}
                className={`px-2 py-0.5 rounded-full transition-colors ${
                  lang === l.code
                    ? light
                      ? "bg-ink text-bg"
                      : "bg-white text-ink"
                    : "opacity-70 hover:opacity-100"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>
          <Link
            to={user ? "/account" : "/login"}
            data-testid="header-account"
            aria-label={user ? t("auth.my_account") : t("auth.sign_in")}
            className={`hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs uppercase tracking-widest2 border transition-colors btn-ghost ${
              light
                ? "border-ink text-ink hover:bg-ink hover:text-bg"
                : "border-white/60 text-white hover:bg-white hover:text-ink"
            }`}
          >
            <User size={14} strokeWidth={1.6} />
            {user ? user.name.split(" ")[0] : t("auth.sign_in")}
          </Link>

          {/* Mobile burger */}
          <button
            type="button"
            onClick={() => setMobileOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
            aria-expanded={mobileOpen}
            className={`md:hidden w-10 h-10 -mr-2 flex items-center justify-center rounded-full ${
              light ? "text-ink" : "text-white"
            }`}
          >
            {mobileOpen ? <X size={22} strokeWidth={1.6} /> : <Menu size={22} strokeWidth={1.6} />}
          </button>
        </div>
      </div>
    </header>

    {/* Mobile drawer — rendered as sibling of header to avoid containing-block issues from backdrop-filter */}
    <div
      data-testid="mobile-menu"
      aria-hidden={!mobileOpen}
      style={{
        transform: mobileOpen ? "translateY(0)" : "translateY(-110%)",
        transition: "transform 300ms cubic-bezier(0.2, 0.6, 0.2, 1)",
        top: "80px",
      }}
      className={`md:hidden fixed inset-x-0 bottom-0 bg-bg z-40 ${
        mobileOpen ? "" : "pointer-events-none"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 py-8 h-full overflow-y-auto flex flex-col">
        <nav className="flex flex-col gap-1">
          {NAV_ITEMS.map(([to, key, testid]) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`mobile-${testid}`}
              className={({ isActive }) =>
                `text-2xl font-serif py-4 border-b border-line ${
                  isActive ? "text-accent" : "text-ink"
                }`
              }
            >
              {t(key)}
            </NavLink>
          ))}
        </nav>

        <div className="mt-8 flex items-center gap-1 rounded-full bg-white/70 border border-line px-1.5 py-1 self-start text-sm">
          {LANGS.map((l) => (
            <button
              key={l.code}
              data-testid={`mobile-lang-${l.code}`}
              onClick={() => setLang(l.code)}
              className={`px-3 py-1 rounded-full transition-colors ${
                lang === l.code ? "bg-ink text-bg" : "text-ink opacity-70"
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="mt-8 space-y-3">
          {user ? (
            <Link
              to="/account"
              data-testid="mobile-account-link"
              className="w-full inline-flex justify-center items-center gap-2 py-3.5 border border-ink text-ink text-xs uppercase tracking-widest2 hover:bg-ink hover:text-bg transition-colors"
            >
              <User size={14} /> {t("auth.my_account")}
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="mobile-signin-link"
                className="w-full inline-flex justify-center items-center gap-2 py-3.5 border border-ink text-ink text-xs uppercase tracking-widest2 hover:bg-ink hover:text-bg transition-colors"
              >
                <User size={14} /> {t("auth.sign_in")}
              </Link>
              <Link
                to="/register"
                data-testid="mobile-register-link"
                className="w-full inline-flex justify-center items-center gap-2 py-3.5 bg-ink text-bg text-xs uppercase tracking-widest2 hover:bg-accent transition-colors"
              >
                {t("auth.create_account")}
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
    </>
  );
}
